import {Component, ContentChild, HostListener, inject, Input, OnInit, TemplateRef} from '@angular/core';
import {ActivitiesService} from '@modules/activities/core/activities.service';
import {ActivityGranule} from '@modules/activities/core/models';
import {BehaviorSubject, Observable, ReplaySubject, scan} from 'rxjs';
import {debounceTime, map, startWith, take} from 'rxjs/operators';
import {ActivitySearchOptions} from '../activity-search-options';

const ACTIVITIES_PER_PAGE = 12;
const MINIMUM_ACTIVITY_THRESHOLD_BEFORE_AUTO_LOAD = 5;

@Component({
    selector: 'app-activities-search-viewer',
    templateUrl: './activities-search-viewer.component.html',
    styleUrls: ['./activities-search-viewer.component.scss'],
    standalone: false
})
export class ActivitiesSearchViewerComponent implements OnInit {

    @Input() activitySearchOptions!: ActivitySearchOptions;
    @ContentChild(TemplateRef) templateRef: TemplateRef<any>;

    public isLoading = true;
    private activities$: BehaviorSubject<ActivityGranule[]> = new BehaviorSubject<ActivityGranule[]>([]);
    public activitiesAcc$ = this.activities$.pipe(scan((acc, value) => [...acc, ...value], []));

    private activitiesService = inject(ActivitiesService);
    private hasNextPage = true;
    private pageNumber = 1;
    private isShouldLoadNextPage$ = new ReplaySubject<void>(1);

    ngOnInit() {
        this.subscribeToLoadNextPage();
    }

    @HostListener('scroll', ['$event'])
    onScroll(event: Event) {
        if (!this.isLoading && this.isNearEndOfScroll(event)) {
            this.isShouldLoadNextPage$.next();
        }
    }

    private subscribeToLoadNextPage() {
        this.isShouldLoadNextPage$.pipe(
            startWith(null),
            debounceTime(500)
        ).subscribe(() => {
            this.loadNextPage();
        });
    }

    private isNearEndOfScroll(event: Event) {
        const element = event.target as HTMLElement;
        const scrollPosition = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        const limitMargin = clientHeight;

        const nearEndThreshold = scrollHeight - clientHeight;
        const triggerThreshold = nearEndThreshold - limitMargin;

        return scrollPosition >= triggerThreshold;
    }

    private loadNextPage() {
        this.isLoading = true;
        const filters = this.generateFilters();

        if (!this.hasNextPage) {
            this.isLoading = false;
            return;
        }

        this.subscribeToActivitiesObservable(this.getPaginatedActivities(filters, this.pageNumber));
    }

    private subscribeToActivitiesObservable(activitiesObservable: Observable<{ entities: ActivityGranule[], hasNextPage: boolean }>) {
        activitiesObservable.pipe(take(1)).subscribe(({entities, hasNextPage}) => {
            this.activities$.next(entities.slice());
            this.updatePageState(hasNextPage, entities);
            this.isLoading = false;
        });
    }

    private updatePageState(hasNextPage: boolean, currentActivities: ActivityGranule[]) {
        this.hasNextPage = hasNextPage;

        if (hasNextPage) {
            this.pageNumber++;
        }

        if (currentActivities.length <= MINIMUM_ACTIVITY_THRESHOLD_BEFORE_AUTO_LOAD && this.hasNextPage) {
            this.loadNextPage();
        }
    }

    private getPaginatedActivities(filters: Partial<{
        concepts: string | number;
        level: string | number;
        chapters: string | number;
    }>, pageNumber: number) {
        return this.activitiesService.loadPaginatedActivities({
            filter: filters,
            range: ACTIVITIES_PER_PAGE,
            page: pageNumber
        }, 'basic_search').pipe(
            map(({entities, paginator}) => ({
                entities,
                hasNextPage: paginator.hasNextPage
            }))
        );
    }

    private generateFilters(): Partial<{
        concepts: string | number,
        level: string | number,
        chapters: string | number,
    }> {
        const filters = {};

        if (this.activitySearchOptions?.concepts?.length) {
            filters['concepts'] = this.activitySearchOptions.concepts.map(concept => concept.id);
        }
        if (this.activitySearchOptions?.educationalLevels?.length) {
            filters['level'] = this.activitySearchOptions.educationalLevels.map(level => level.id);
        }
        if (this.activitySearchOptions?.chapters?.length) {
            filters['chapters'] = this.activitySearchOptions.chapters.map(chapter => chapter.id);
        }

        return filters;
    }

    public hasMorePageToLoad() {
        return this.hasNextPage;
    }
}
